/**
 * Audit Logs Report Component
 * 21 CFR Part 11 Compliance - Comprehensive Audit Logging
 *
 * Redesigned with Ant Design, Tailwind CSS, and Lucide Icons
 * - Checkbox selection instead of row collapse
 * - Right-side drawer for detailed view
 * - Pharmaceutical industry standard design
 */

import React, { useState, useEffect } from 'react';
import {
  Tag,
  Button,
  Input,
  DatePicker,
  Select,
  Space,
  Card,
  Descriptions,
  Divider,
  Tabs,
  Spin,
  Empty,
  Alert,
  Row,
  Col,
  Typography,
  Checkbox,
  Collapse,
} from 'antd';
import {
  Shield,
  Search,
  RefreshCw,
  Download,
  Lock,
  Database,
  AlertTriangle,
  UserCheck,
  FileCheck,
  Info,
  Calendar,
  User,
  Building,
  CheckCircle2,
  XCircle,
  Clock,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { downloadAuditExport, AUDIT_TAB_SECTIONS } from '@/utils/auditExport';
import {
  get_security_events,
  get_master_data_changes,
  get_data_integrity_logs,
  get_permission_changes,
  get_audit_reviews,
} from '@/utils/api';
import { defaultColors } from '@/themes/antd-theme';
import { useTheme } from '@/App';
import DataTable from '@/components/DataTable';
import PageHeader from '@/components/PageHeader';
import PageModal from '@/components/PageModal';
import { formatDateTime } from '@/utils/datetime';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

/**
 * Severity color mapping for pharmaceutical compliance
 */
const getSeverityConfig = (severity) => {
  const configs = {
    CRITICAL: { color: 'red', className: 'bg-red-50 border-red-200' },
    HIGH: { color: 'orange', className: 'bg-orange-50 border-orange-200' },
    MEDIUM: { color: 'cyan', className: 'bg-gray-50 border-gray-200' },
    LOW: { color: 'green', className: 'bg-green-50 border-green-200' },
    INFO: { color: 'default', className: 'bg-gray-50 border-gray-200' },
  };
  return configs[severity] || configs['INFO'];
};

/**
 * Render JSON data as Ant Design Descriptions
 */
function renderJsonAsDescriptions(data, title) {
  if (!data || typeof data !== 'object' || Object.keys(data).length === 0) {
    return <Text type="secondary">No data available</Text>;
  }

  return (
    <div className="mt-4">
      {title && (
        <Text strong className="text-gray-600 mb-2 block">
          {title}
        </Text>
      )}
      <Descriptions bordered size="small" column={1} className="bg-white">
        {Object.entries(data).map(([key, value]) => (
          <Descriptions.Item
            key={key}
            label={
              <span className="font-semibold text-gray-700">
                {key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
              </span>
            }
          >
            <span className="font-mono text-sm">
              {typeof value === 'object' && value !== null
                ? JSON.stringify(value, null, 2)
                : String(value || 'N/A')}
            </span>
          </Descriptions.Item>
        ))}
      </Descriptions>
    </div>
  );
}

/**
 * Render audit findings as cards
 */
function renderFindings(findings) {
  if (!findings || findings.length === 0) {
    return <Text type="secondary">No findings reported</Text>;
  }

  return (
    <div className="space-y-3">
      {findings.map((finding, idx) => {
        let findingData = finding;
        if (typeof finding === 'string') {
          try {
            findingData = JSON.parse(finding);
          } catch (e) {
            findingData = { description: finding };
          }
        }

        const severity = findingData.severity || 'MEDIUM';
        const config = getSeverityConfig(severity);

        return (
          <Card key={idx} size="small" className={`border-l-4 ${config.className}`}>
            <div className="space-y-2">
              <div className="flex justify-between items-start">
                <Space>
                  <Tag color={config.color} className="font-semibold">
                    {severity}
                  </Tag>
                  {findingData.record_id && (
                    <Tag className="font-mono">ID: {findingData.record_id}</Tag>
                  )}
                </Space>
                <Text type="secondary" className="text-xs">
                  Finding #{idx + 1}
                </Text>
              </div>

              <Paragraph className="mb-2 font-medium">
                {findingData.description || findingData.finding || 'No description provided'}
              </Paragraph>

              {(findingData.details || findingData.notes || findingData.action_required) && (
                <div className="pt-2 border-t space-y-2">
                  {findingData.details && (
                    <div>
                      <Text type="secondary" className="text-xs block">
                        Details:
                      </Text>
                      <Text className="text-sm">{findingData.details}</Text>
                    </div>
                  )}
                  {findingData.notes && (
                    <div>
                      <Text type="secondary" className="text-xs block">
                        Notes:
                      </Text>
                      <Text className="text-sm">{findingData.notes}</Text>
                    </div>
                  )}
                  {findingData.action_required && (
                    <Alert
                      message={`Action Required: ${findingData.action_required}`}
                      type="warning"
                      showIcon
                      className="mt-2"
                    />
                  )}
                </div>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

/**
 * Drawer Content Component - Displays detailed audit information
 */
function AuditDetailDrawer({ visible, onClose, record, type }) {
  if (!record) return null;

  const renderSecurityDetails = () => (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="bg-gray-50 border-l-4 border-gray-300">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <Text type="secondary" className="text-xs block mb-1">
              Event ID
            </Text>
            <Text strong className="font-mono">
              {record.event_id || record.id}
            </Text>
          </div>
          <div>
            <Text type="secondary" className="text-xs block mb-1">
              Timestamp
            </Text>
            <Text strong>
              {formatDateTime(record.event_timestamp || record.timestamp, 'YYYY-MM-DD HH:mm:ss')}
            </Text>
          </div>
          <div>
            <Text type="secondary" className="text-xs block mb-1">
              Manufacturing Site
            </Text>
            <Text strong>{record.plant?.plant_name || 'N/A'}</Text>
          </div>
          <div>
            <Text type="secondary" className="text-xs block mb-1">
              Status
            </Text>
            <Tag color={record.success ? 'success' : 'error'} className="font-semibold">
              {record.success ? 'Success' : 'Failed'}
            </Tag>
          </div>
        </div>
      </Card>

      {/* System Access Information */}
      <div>
        <Title level={5} className="text-gray-600 flex items-center gap-2">
          <Lock size={18} /> System Access Information
        </Title>
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label="Operator ID">
            <span className="font-mono">{record.username || record.user?.username || 'N/A'}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Action Performed">
            {record.event_type || 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Network Location">
            <span className="font-mono">{record.ip_address || 'N/A'}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Session Identifier">
            <span className="font-mono text-xs break-all">{record.session_key || 'N/A'}</span>
          </Descriptions.Item>
          {record.failure_reason && (
            <Descriptions.Item label="Failure Reason">
              <Text type="danger" strong>
                {record.failure_reason}
              </Text>
            </Descriptions.Item>
          )}
        </Descriptions>
      </div>

      {/* Client System Information */}
      <div>
        <Title level={5} className="text-gray-600">
          Client System Information
        </Title>
        <Card size="small" className="bg-gray-50">
          <Text type="secondary" className="text-xs block mb-2">
            User Agent String:
          </Text>
          <Text className="font-mono text-xs break-all">{record.user_agent || 'N/A'}</Text>
        </Card>
      </div>

      {/* Additional Details */}
      {record.details &&
        Object.keys(record.details).length > 0 &&
        renderJsonAsDescriptions(record.details, 'Additional Context & Metadata')}
    </div>
  );

  const renderMasterDataDetails = () => (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="bg-gray-50 border-l-4 border-gray-300">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <Text type="secondary" className="text-xs block mb-1">
              Change Control ID
            </Text>
            <Text strong className="font-mono">
              {record.change_id || record.id}
            </Text>
          </div>
          <div>
            <Text type="secondary" className="text-xs block mb-1">
              Change Type
            </Text>
            <Tag>{record.operation}</Tag>
          </div>
          <div>
            <Text type="secondary" className="text-xs block mb-1">
              Data Category
            </Text>
            <Text strong>{record.master_data_type || record.event_type}</Text>
          </div>
          <div>
            <Text type="secondary" className="text-xs block mb-1">
              Record ID
            </Text>
            <Text strong className="font-mono">
              {record.record_id || 'N/A'}
            </Text>
          </div>
        </div>
      </Card>

      <Paragraph type="secondary" italic>
        {record.record_description || 'No description provided'}
      </Paragraph>

      {/* Business Justification */}
      {record.reason && (
        <Card className="bg-gray-50 border-l-4 border-gray-300">
          <Title level={5} className="text-gray-600 mb-2">
            Business Justification (21 CFR Part 11.10)
          </Title>
          <Paragraph>{record.reason}</Paragraph>
        </Card>
      )}

      {/* Change Comparison */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <Title level={5} className="mb-0">
            Data Change Comparison
          </Title>
          <Tag color={getSeverityConfig(record.impact_level || record.severity).color}>
            Impact: {record.impact_level || record.severity || 'Medium'}
          </Tag>
          {record.affects_active_batches && <Tag color="warning">⚠ Affects Active Batches</Tag>}
        </div>

        <Row gutter={16}>
          <Col span={12}>
            <Card
              title={<Text type="secondary">← PREVIOUS STATE</Text>}
              size="small"
              className="bg-gray-50"
            >
              {renderJsonAsDescriptions(record.old_data, null)}
            </Card>
          </Col>
          <Col span={12}>
            <Card
              title={<Text className="text-green-600">CURRENT STATE →</Text>}
              size="small"
              className="bg-green-50"
            >
              {renderJsonAsDescriptions(record.new_data, null)}
            </Card>
          </Col>
        </Row>
      </div>

      {/* Field Changes */}
      {record.fields_changed && Object.keys(record.fields_changed).length > 0 && (
        <div>
          <Alert
            message={`${
              Object.keys(record.fields_changed).length
            } field(s) modified in this change`}
            type="info"
            showIcon
            className="mb-3"
          />
          {renderJsonAsDescriptions(record.fields_changed, 'Field-Level Change Summary')}
        </div>
      )}

      {/* Audit Metadata */}
      <Divider />
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Text type="secondary" className="text-xs block">
            Modified By:
          </Text>
          <Text strong>{record.changed_by?.username || record.user?.username || 'N/A'}</Text>
        </div>
        <div>
          <Text type="secondary" className="text-xs block">
            Timestamp:
          </Text>
          <Text strong>
            {formatDateTime(record.timestamp || record.event_timestamp, 'YYYY-MM-DD HH:mm:ss')}
          </Text>
        </div>
        <div>
          <Text type="secondary" className="text-xs block">
            Manufacturing Site:
          </Text>
          <Text strong>{record.plant?.plant_name || 'N/A'}</Text>
        </div>
      </div>
    </div>
  );

  const renderDataIntegrityDetails = () => (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="bg-red-50 border-l-4 border-red-500">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <Text type="secondary" className="text-xs block mb-1">
              Integrity Event ID
            </Text>
            <Text strong className="font-mono">
              {record.integrity_id || record.event_id || record.id}
            </Text>
          </div>
          <div>
            <Text type="secondary" className="text-xs block mb-1">
              Issue Type
            </Text>
            <Tag color="error">{record.event_type}</Tag>
          </div>
          <div>
            <Text type="secondary" className="text-xs block mb-1">
              Severity
            </Text>
            <Tag color={getSeverityConfig(record.severity).color}>{record.severity}</Tag>
          </div>
          <div>
            <Text type="secondary" className="text-xs block mb-1">
              Status
            </Text>
            <Tag color={record.is_resolved ? 'success' : 'error'} className="font-semibold">
              {record.is_resolved ? 'Closed' : 'Open'}
            </Tag>
          </div>
        </div>
      </Card>

      {/* Issue Description */}
      <Alert
        message="Deviation/Non-Conformance Description"
        description={record.description || 'N/A'}
        type="warning"
        showIcon
      />

      {/* Affected System Details */}
      <Row gutter={16}>
        <Col span={12}>
          <Title level={5} className="text-red-600">
            Affected System & Data
          </Title>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Database Table">
              <span className="font-mono">{record.table_name || 'N/A'}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Record Identifier">
              <span className="font-mono">{record.record_id || 'N/A'}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Field Name">
              <span className="font-mono">{record.field_name || 'N/A'}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Detection Method">
              {record.detected_by || 'Automated System Check'}
            </Descriptions.Item>
            <Descriptions.Item label="Detection Timestamp">
              {formatDateTime(record.detected_timestamp || record.timestamp, 'YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
          </Descriptions>
        </Col>
        <Col span={12}>
          <Title level={5} className="text-red-600">
            Validation Check Results
          </Title>
          <div className="space-y-3">
            <Card size="small" className="bg-green-50 border-l-4 border-green-500">
              <Text type="secondary" className="text-xs block mb-1">
                ✓ EXPECTED VALUE
              </Text>
              <Text className="font-mono text-sm break-all">
                {record.expected_value || 'Not specified'}
              </Text>
            </Card>
            <Card size="small" className="bg-red-50 border-l-4 border-red-500">
              <Text type="danger" className="text-xs block mb-1">
                ✗ ACTUAL VALUE
              </Text>
              <Text className="font-mono text-sm break-all">
                {record.actual_value || 'Not recorded'}
              </Text>
            </Card>
          </div>
        </Col>
      </Row>

      {/* Impact Assessment */}
      {record.affected_records && Object.keys(record.affected_records).length > 0 && (
        <Alert
          message="Impact Assessment"
          description={renderJsonAsDescriptions(record.affected_records, null)}
          type="error"
          showIcon
          icon={<AlertTriangle size={20} />}
        />
      )}

      {/* Resolution Information */}
      {record.is_resolved ? (
        <Card className="bg-green-50 border-l-4 border-green-500">
          <Title level={5} className="text-green-600 mb-3">
            ✓ Corrective Action & Preventive Action (CAPA)
          </Title>
          <Row gutter={16}>
            <Col span={12}>
              <Text type="secondary" className="text-xs block">
                Resolved By:
              </Text>
              <Text strong>{record.resolved_by || 'N/A'}</Text>
            </Col>
            <Col span={12}>
              <Text type="secondary" className="text-xs block">
                Resolution Date:
              </Text>
              <Text strong>
                {record.resolved_timestamp
                  ? formatDateTime(record.resolved_timestamp, 'YYYY-MM-DD HH:mm:ss')
                  : 'N/A'}
              </Text>
            </Col>
            {record.resolution_notes && (
              <Col span={24} className="mt-3">
                <Text type="secondary" className="text-xs block">
                  Resolution Notes:
                </Text>
                <Paragraph>{record.resolution_notes}</Paragraph>
              </Col>
            )}
          </Row>
        </Card>
      ) : (
        <Alert
          message="⚠ This deviation remains OPEN and requires investigation and resolution"
          type="error"
          showIcon
        />
      )}
    </div>
  );

  const renderPermissionDetails = () => (
    <div className="space-y-6">
      <div className="flex gap-2">
        <Tag>{record.change_type}</Tag>
        {record.affected_role && <Tag>{record.affected_role}</Tag>}
      </div>

      {record.reason && (
        <Card className="bg-gray-50">
          <Title level={5}>Reason</Title>
          <Paragraph>{record.reason}</Paragraph>
        </Card>
      )}

      <Row gutter={16}>
        <Col span={12}>
          <Title level={5}>Permission Change Details</Title>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Changed By">
              {record.changed_by?.username || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Changed By Role">
              {record.changed_by_role || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Old Value">{record.old_value || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="New Value">
              <Text className="text-green-600 font-semibold">{record.new_value || 'N/A'}</Text>
            </Descriptions.Item>
          </Descriptions>
        </Col>
        {record.permissions_changed && record.permissions_changed.length > 0 && (
          <Col span={12}>
            <Title level={5}>Permissions Changed</Title>
            <Card size="small" className="max-h-64 overflow-auto">
              <Space wrap>
                {record.permissions_changed.map((perm, idx) => (
                  <Tag key={idx}>{perm}</Tag>
                ))}
              </Space>
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );

  const renderReviewDetails = () => (
    <div className="space-y-6">
      <Row gutter={16}>
        <Col span={12}>
          <Title level={5}>Review Information</Title>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Review Type">
              {record.review_type || record.event_type}
            </Descriptions.Item>
            <Descriptions.Item label="Audit Log Type">
              {record.audit_log_type || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Reviewer Role">
              {record.reviewer_role || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Period Start">
              {record.period_start || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Period End">{record.period_end || 'N/A'}</Descriptions.Item>
          </Descriptions>
        </Col>
        <Col span={12}>
          <Title level={5}>Review Statistics</Title>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Records Reviewed">
              {record.records_reviewed || 0}
            </Descriptions.Item>
            <Descriptions.Item label="Issues Found">
              <Tag color={record.issues_found > 0 ? 'warning' : 'success'}>
                {record.issues_found || 0}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        </Col>
      </Row>

      {record.notes && (
        <Card className="bg-gray-50">
          <Title level={5}>Notes</Title>
          <Paragraph>{record.notes}</Paragraph>
        </Card>
      )}

      {record.findings && record.findings.length > 0 && (
        <div>
          <Title level={5}>Audit Findings</Title>
          {renderFindings(record.findings)}
        </div>
      )}
    </div>
  );

  const renderContent = () => {
    switch (type) {
      case 'security':
        return renderSecurityDetails();
      case 'master-data':
        return renderMasterDataDetails();
      case 'data-integrity':
        return renderDataIntegrityDetails();
      case 'permission':
        return renderPermissionDetails();
      case 'review':
        return renderReviewDetails();
      default:
        return renderJsonAsDescriptions(record, 'Record Details');
    }
  };

  return (
    <PageModal
      icon={<Shield size={22} />}
      title="Audit Record Details"
      description="21 CFR Part 11 compliance record"
      onCancel={onClose}
      open={visible}
      width={820}
      destroyOnHidden
    >
      {renderContent()}
    </PageModal>
  );
}

/**
 * Audit Section Component with Table and Selection
 */
function AuditSection({ title, icon: Icon, type, apiFn, color, description, isDark, cardBgColor, textColor, secondaryTextColor }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });
  const [exporting, setExporting] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    severity: '',
    username: '',
  });

  // Server-side CSV export of the whole filtered section (F25 — the button previously had no onClick).
  const handleExport = async () => {
    setExporting(true);
    await downloadAuditExport(AUDIT_TAB_SECTIONS[type] || type, {
      start_date: filters.startDate ? filters.startDate.format('YYYY-MM-DD') : undefined,
      end_date: filters.endDate ? filters.endDate.format('YYYY-MM-DD') : undefined,
      severity: filters.severity,
      username: filters.username,
    });
    setExporting(false);
  };

  /**
   * Fetch data from API
   */
  const fetchData = async (page = 1, pageSize = 15, customFilters = null) => {
    setLoading(true);
    const activeFilters = customFilters || filters;

    try {
      const params = {
        page,
        page_size: pageSize,
        ...(activeFilters.startDate && {
          start_date: activeFilters.startDate.format('YYYY-MM-DD'),
        }),
        ...(activeFilters.endDate && { end_date: activeFilters.endDate.format('YYYY-MM-DD') }),
        ...(activeFilters.severity && { severity: activeFilters.severity }),
        ...(activeFilters.username && { username: activeFilters.username }),
      };

      await apiFn(params, (response) => {
        if (response?.data?.status === 'success' || response?.data?.results) {
          setData(response.data.results || response.data.data || []);
          setPagination({
            current: page,
            pageSize,
            total: response.data.count || response.data.total || 0,
          });
        } else {
          toast.error('Failed to fetch data');
          setData([]);
          setPagination({
            current: page,
            pageSize,
            total: 0,
          });
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error fetching data');
      setLoading(false);
    }
  };

  /**
   * Handle search
   */
  const handleSearch = () => {
    fetchData(1, pagination.pageSize, filters);
  };

  /**
   * Handle reset
   */
  const handleReset = () => {
    const resetFilters = {
      startDate: null,
      endDate: null,
      severity: '',
      username: '',
    };
    setFilters(resetFilters);
    // Fetch data with reset filters immediately
    fetchData(1, pagination.pageSize, resetFilters);
  };

  /**
   * Handle row selection
   */
  const handleRowSelect = (record) => {
    setSelectedRecord(record);
    setDrawerVisible(true);
  };

  // Fetch data on mount
  useEffect(() => {
    fetchData(1, 15, filters);
  }, []);

  // Table columns
  const columns = [
    {
      title: '',
      key: 'selection',
      width: 48,
      fixed: 'left',
      render: (_, record) => (
        <Checkbox
          checked={selectedRecord?.id === record.id && drawerVisible}
          onChange={() => handleRowSelect(record)}
        />
      ),
    },
    {
      title: (
        <div className="flex items-center gap-2">
          <Clock size={14} /> Timestamp
        </div>
      ),
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 190,
      render: (_, record) => (
        <span className="font-mono text-sm">
          {formatDateTime(
            record.event_timestamp || record.timestamp || record.detected_timestamp,
            'YYYY-MM-DD HH:mm:ss'
          )}
        </span>
      ),
    },
    {
      title: 'Event/Type',
      key: 'event_type',
      width: 220,
      render: (_, record) => (
        <span className="font-medium">
          {record.event_type || record.master_data_type || record.operation || 'N/A'}
        </span>
      ),
    },
    {
      title: 'Severity',
      key: 'severity',
      width: 110,
      render: (_, record) => {
        const severity = record.severity || record.impact_level || 'INFO';
        const config = getSeverityConfig(severity);
        return (
          <Tag color={config.color} className="font-semibold">
            {severity}
          </Tag>
        );
      },
    },
    {
      title: (
        <div className="flex items-center gap-2">
          <User size={14} /> User
        </div>
      ),
      key: 'user',
      width: 180,
      render: (_, record) => (
        <span className="font-medium">
          {record.user?.username ||
            record.changed_by?.username ||
            record.detected_by?.username ||
            'N/A'}
        </span>
      ),
    },
    {
      title: (
        <div className="flex items-center gap-2">
          <Building size={14} /> Plant
        </div>
      ),
      key: 'plant',
      width: 200,
      render: (_, record) => record.plant?.plant_name || 'N/A',
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, record) => {
        if (record.success !== undefined) {
          return (
            <Tag
              color={record.success ? 'success' : 'error'}
              icon={record.success ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
              className="font-semibold"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, margin: 0, whiteSpace: 'nowrap' }}
            >
              {record.success ? 'Success' : 'Failed'}
            </Tag>
          );
        }
        return <Tag>N/A</Tag>;
      },
    },
  ];

  const labelStyle = { color: secondaryTextColor, fontSize: 11, fontWeight: 600, letterSpacing: '.03em', textTransform: 'uppercase' };

  return (
    <div className="space-y-3">
      {/* Filters — one compact row, labels above the inputs */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <div style={labelStyle}>Date range</div>
          <RangePicker
            style={{ width: 280, height: 36 }}
            value={[filters.startDate, filters.endDate]}
            onChange={(dates) => setFilters({ ...filters, startDate: dates?.[0], endDate: dates?.[1] })}
            format="YYYY-MM-DD"
          />
        </div>
        <div>
          <div style={labelStyle}>Severity</div>
          <Select
            style={{ width: 150 }}
            value={filters.severity}
            onChange={(value) => setFilters({ ...filters, severity: value })}
            placeholder="All"
          >
            <Option value="">All</Option>
            <Option value="CRITICAL">Critical</Option>
            <Option value="HIGH">High</Option>
            <Option value="MEDIUM">Medium</Option>
            <Option value="LOW">Low</Option>
            <Option value="INFO">Info</Option>
          </Select>
        </div>
        <div>
          <div style={labelStyle}>Username</div>
          <Input
            style={{ width: 200 }}
            value={filters.username}
            onChange={(e) => setFilters({ ...filters, username: e.target.value })}
            onPressEnter={handleSearch}
            placeholder="Enter username"
            allowClear
          />
        </div>
        <Button type="primary" icon={<Search size={16} />} onClick={handleSearch} loading={loading}>Search</Button>
        <Button icon={<RefreshCw size={16} />} onClick={handleReset}>Reset</Button>
      </div>

      {/* Table — paged by the server */}
      <DataTable
        tableKey={`auditLogs_${type}`}
        serverSide
        searchable
        searchPlaceholder="Search this page…"
        searchKeys={['event_type', 'master_data_type', 'operation', 'severity', 'impact_level']}
        columnConfig
        showCount={false}
        scrollX="max-content"
        toolbarExtra={(
          <Space>
            <Button size="small" icon={<Download size={14} />} disabled={data.length === 0} loading={exporting} onClick={handleExport}>Export</Button>
          </Space>
        )}
        columns={columns}
        data={data}
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: [15, 25, 50, 100],
          showTotal: (n) => `Total ${n} records`,
          onChange: (page, pageSize) => fetchData(page, pageSize, filters),
        }}
        rowKey="id"
        onRow={(record) => ({
          onClick: () => handleRowSelect(record),
          style: {
            backgroundColor: selectedRecord?.id === record.id && drawerVisible
              ? (isDark ? defaultColors.palette.teal[900] : defaultColors.palette.teal[50])
              : 'inherit',
            cursor: 'pointer',
          },
        })}
        emptyMessage="No audit logs found"
        size="small"
        height={null}
        minHeight={320}
        bordered
      />

      {/* Drawer */}
      <AuditDetailDrawer
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        record={selectedRecord}
        type={type}
      />
    </div>
  );
}

/**
 * Main Audit Logs Report Component
 */
export default function AuditLogsReport() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  // Dark mode colors
  const pageBgColor = isDark ? defaultColors.palette.gray[900] : '#f9fafb';
  const cardBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const secondaryTextColor = isDark ? defaultColors.palette.gray[400] : '#6b7280';
  const complianceCardBg = isDark ? defaultColors.palette.gray[700] : '#f9fafb';
  const complianceCardBorder = defaultColors.primary;
  const infoBoxBg = isDark ? defaultColors.palette.gray[700] : '#ffffff';

  const tabConfig = [
    {
      key: 'security',
      label: 'Security Events',
      icon: Lock,
      apiFn: get_security_events,
      color: '#1890ff',
      description: 'Login attempts, access control, and authentication events',
    },
    {
      key: 'master-data',
      label: 'Master Data Changes',
      icon: Database,
      apiFn: get_master_data_changes,
      color: '#722ed1',
      description: 'Changes to critical master data records',
    },
    {
      key: 'data-integrity',
      label: 'Data Integrity',
      icon: AlertTriangle,
      apiFn: get_data_integrity_logs,
      color: '#cf1322',
      description: 'Validation failures and integrity issues',
    },
    {
      key: 'permission',
      label: 'Permission Changes',
      icon: UserCheck,
      apiFn: get_permission_changes,
      color: '#389e0d',
      description: 'User role and permission modifications',
    },
    {
      key: 'review',
      label: 'Audit Reviews',
      icon: FileCheck,
      apiFn: get_audit_reviews,
      color: '#fa8c16',
      description: 'Periodic audit log reviews and findings',
    },
  ];

  return (
    <div className="p-2.5 pt-5" style={{ backgroundColor: pageBgColor }}>
      <Card
        className="border border-gray-300 shadow-md"
        bordered={false}
        classNames={{ body: 'page-card-scroll' }}
        style={{ backgroundColor: cardBgColor, height: 'calc(100dvh - 120px)', display: 'flex', flexDirection: 'column' }}
        styles={{ body: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 16px' } }}
        title={
          <PageHeader
            compact
            icon={<FileCheck size={22} />}
            title={t('Audit Logs Report')}
            description="Computer-generated, time-stamped audit trails: security events, master data changes, data integrity, permissions and reviews."
          />
        }
      >
        {/* Compliance Notice — collapsible to save vertical space (collapsed by default) */}
        <Collapse
          ghost
          className="er-audit-collapse border-l-4"
          style={{ backgroundColor: complianceCardBg, borderLeftColor: complianceCardBorder, borderRadius: 8, marginBottom: 12 }}
          items={[
            {
              key: 'compliance',
              label: (
                <div className="flex items-center gap-3">
                  <Shield size={22} style={{ color: defaultColors.primary }} />
                  <div>
                    <Text strong style={{ color: textColor }} className="text-base block leading-tight">
                      Electronic Records Audit Trail
                    </Text>
                    <Text style={{ color: secondaryTextColor }} className="text-xs">
                      21 CFR Part 11 Compliance - FDA Electronic Records Regulation
                    </Text>
                  </div>
                </div>
              ),
              children: (
                <div className="space-y-3">
                  <Paragraph style={{ color: textColor, fontSize: 13, marginBottom: 0 }}>
                    <strong>NOTE:</strong> This system maintains comprehensive, computer-generated, time-stamped audit
                    trails to independently record the date and time of operator entries and actions that create,
                    modify, or delete electronic records in compliance with{' '}
                    <Tag className="font-bold" style={{ marginInlineEnd: 0 }}>21 CFR Part 11.10(e)</Tag>.
                  </Paragraph>

                  <div>
                    <Text style={{ color: secondaryTextColor, fontSize: 12, fontWeight: 600, letterSpacing: '.03em', textTransform: 'uppercase' }} className="block mb-2">
                      Each audit trail entry captures
                    </Text>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
                      {[
                        [User, 'WHO', 'Operator ID'],
                        [FileCheck, 'WHAT', 'Action Performed'],
                        [Clock, 'WHEN', 'Timestamp'],
                        [Building, 'WHERE', 'Manufacturing Site'],
                        [Info, 'WHY', 'Business Justification'],
                      ].map(([ItemIcon, label, text]) => (
                        <div
                          key={label}
                          className="flex items-center gap-3"
                          style={{ padding: '10px 14px', borderRadius: 10, border: `1px solid ${isDark ? defaultColors.palette.gray[600] : '#e5e7eb'}`, backgroundColor: infoBoxBg }}
                        >
                          <span
                            className="flex items-center justify-center shrink-0"
                            style={{ width: 34, height: 34, borderRadius: '50%', backgroundColor: `${defaultColors.primary}22`, color: defaultColors.primary }}
                          >
                            <ItemIcon size={17} />
                          </span>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: textColor, lineHeight: '18px' }}>{label}</div>
                            <div style={{ fontSize: 12, color: secondaryTextColor, lineHeight: '16px' }}>{text}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ),
            },
          ]}
        />
        <Tabs
          defaultActiveKey="security"
          size="large"
          tabBarStyle={{ padding: '0 8px', marginBottom: 12 }}
          items={tabConfig.map((tab) => ({
            key: tab.key,
            label: (
              <div className="flex items-center gap-3 py-2 px-1">
                <tab.icon size={20} style={{ color: tab.color }} />
                <div className="text-left">
                  <div className="font-semibold" style={{ color: textColor }}>{tab.label}</div>
                  <div className="text-xs font-normal" style={{ color: secondaryTextColor }}>{tab.description}</div>
                </div>
              </div>
            ),
            children: (
              <AuditSection
                title={tab.label}
                icon={tab.icon}
                type={tab.key}
                apiFn={tab.apiFn}
                color={tab.color}
                description={tab.description}
                isDark={isDark}
                cardBgColor={cardBgColor}
                textColor={textColor}
                secondaryTextColor={secondaryTextColor}
              />
            ),
          }))}
        />
      </Card>
    </div>
  );
}
